import { ChargeComponent } from '@/dto/subscription/SublimeChargeDTO';

/**
 * Centralized constraints for Subscription Plan components: mutual exclusions, etc.
 *
 * Current requirement: some components are mutually exclusive. As of now, the
 * mutually exclusive group includes PAYROLL_MONTHLY_FEE and PAYROLL_QUARTERLY_FEE.
 * In the future, this group may also include PAYROLL_ANNUAL_FEE and PAYROLL_WEEKLY_FEE,
 * and other groups may be added. This utility is designed to be easily extended.
 */

// Helper: get existing enum values as strings
function enumValues(): Set<string> {
  return new Set<string>(Object.values(ChargeComponent) as string[]);
}

// Define candidate groups by string names to allow future values without breaking TS
const CANDIDATE_GROUPS: Array<Array<string>> = [
  // Payroll subscription base fees are mutually exclusive (period-based)
  ['PAYROLL_MONTHLY_FEE', 'PAYROLL_QUARTERLY_FEE', 'PAYROLL_ANNUAL_FEE', 'PAYROLL_WEEKLY_FEE'],
  // Additional groups can be added here later, e.g. ['X', 'Y']
];

// Normalize groups to only include values that exist in the current enum
const GROUPS: Array<Set<ChargeComponent>> = (() => {
  const values = enumValues();
  return CANDIDATE_GROUPS
    .map(group => new Set<ChargeComponent>(group.filter(n => values.has(n)) as ChargeComponent[]))
    .filter(set => set.size >= 2); // keep only meaningful groups
})();

/**
 * Returns all mutually exclusive groups (only with currently existing enum values).
 */
export function getMutuallyExclusiveGroups(): Array<Set<ChargeComponent>> {
  return GROUPS;
}

/**
 * Returns true if two components belong to the same mutually exclusive group.
 */
export function areMutuallyExclusive(a?: ChargeComponent | null, b?: ChargeComponent | null): boolean {
  if (!a || !b) return false;
  return GROUPS.some(group => group.has(a) && group.has(b));
}

/**
 * Given already selected components, returns a set of components that are excluded from selection
 * (i.e., they are mutually exclusive with at least one of the selected components).
 */
export function getExcludedBy(selected: Array<ChargeComponent | null | undefined>): Set<ChargeComponent> {
  const result = new Set<ChargeComponent>();
  const selectedSet = new Set(selected.filter(Boolean) as ChargeComponent[]);
  for (const group of GROUPS) {
    if ([...group].some(c => selectedSet.has(c))) {
      // if any member of the group is selected, exclude the rest
      for (const c of group) {
        if (!selectedSet.has(c)) result.add(c);
      }
    }
  }
  return result;
}

export default {
  getMutuallyExclusiveGroups,
  areMutuallyExclusive,
  getExcludedBy,
};
